import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { Check, X, ChevronDown, ChevronUp, Calendar, MapPin, Users, Bus, Bed, ArrowLeft, HelpCircle, Building2, CreditCard, Mountain, Flag, ClipboardList } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import { dynamicAPI, bookingAPI, addOnsAPI } from '../services/api';
import { openRazorpayBookingPayment } from '../utils/razorpayCheckout';
import { formatStayOptionLabel } from '../utils/formatStayOptionLabel.js';
import { onlineTotalWithGatewayFee } from '../utils/paymentGateway.js';
import { resolveJoinPackageOrderSummary } from '../utils/joinPackageDuration.js';
import { capitalizeNamePart } from '../utils/capitalizePersonName.js';
import { useAuth } from '../context/AuthContext';
import { formatTrekAgeRangeLabel, validateAgeForTrekRange, dobBoundsForTrekAgeRange } from '../utils/trekAgeRange.js';
import {
  ADVANCE_CAP,
  ADVANCE_WINDOW_DAYS,
  REMAINING_DUE_DAYS_BEFORE_DEPARTURE,
  remainingDueDateFromDeparture
} from '../utils/bookingPaymentPolicy.js';
import { bookingEventClosedForPayment } from '../utils/bookingPayment.js';
import { filterPackagesForLocalCamps } from '../utils/localCampPackages.js';
import { trackBeginCheckout } from '../utils/googleAnalytics.js';
import LoadingIndicator from '../components/LoadingIndicator';
import './Book.css';

const STEPS = [
  { id: 1, label: 'DETAILS' },
  { id: 2, label: 'PAYMENT' }
];

const ADDON_CATEGORIES = {
  gear: 'GEAR RENTALS',
  activity: 'OPTIONAL ACTIVITIES',
  stay: 'ADDITIONAL STAY OPTIONS'
};

const ADDON_DEFAULT_UNIT = 'TRIP';

const MOBILE_DIGITS = 10;

const emptyTraveler = () => ({ firstName: '', lastName: '', phone: '', birthdate: '', gender: '' });

const normalizeMobile = (value) => String(value || '').replace(/\D/g, '').slice(0, MOBILE_DIGITS);
const isValidMobile = (value) => normalizeMobile(value).length === MOBILE_DIGITS;
const toLocalDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatDobDigitsInput = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};
const parseDobDisplayToYmd = (display) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(display || '').trim());
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return null;
  return toLocalDateInput(dt);
};
const birthdateFieldValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-');
    return `${d}/${m}/${y}`;
  }
  return formatDobDigitsInput(raw);
};
const birthdateForApi = (value) => parseDobDisplayToYmd(birthdateFieldValue(value)) || undefined;
const isValidTravelerBirthdate = (value) => Boolean(birthdateForApi(value));
const isValidTravelerGender = (value) => {
  const g = String(value || '').trim().toLowerCase();
  return g === 'male' || g === 'female' || g === 'other';
};
/** Parse `YYYY-MM-DD` at local midnight (same semantics as native date inputs). */
const parseLocalYmd = (ymd) => {
  if (!ymd || typeof ymd !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
};
const inferTravelPackageFromText = (value) => {
  const text = String(value || '').toLowerCase();
  if (!text) return null;
  if (text.includes('flight') || text.includes('air')) return 'flight';
  if (text.includes('train') || text.includes('sleeper') || text.includes('tier') || text.includes('coach')) return 'train';
  if (text.includes('bus') || text.includes('volvo')) return 'bus';
  return null;
};
const plainTextFromHtml = (html) => {
  if (html == null || String(html).trim() === '') return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const DOB_MIN_DATE = new Date(new Date().getFullYear() - 100, 0, 1);

function BookTravelerDobField({
  id,
  value,
  onChange,
  invalid,
  errorMessage,
  travelerIndex,
  minDate = DOB_MIN_DATE,
  maxDate = new Date()
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseLocalYmd(value) : null;
  const openToDate = selected || maxDate || new Date();

  return (
    <div className="book-form-field">
      <label className="book-field-label" htmlFor={id}>
        Date of birth <span className="book-required-star">*</span>
      </label>
      <div className="book-input-icon-wrap">
        <DatePicker
          id={id}
          selected={selected}
          onChange={(date) => {
            onChange(date ? toLocalDateInput(date) : '');
            setOpen(false);
          }}
          open={open}
          onInputClick={() => setOpen(true)}
          onClickOutside={() => setOpen(false)}
          dateFormat="dd/MM/yyyy"
          placeholderText="DD/MM/YYYY"
          className={`book-input book-date-input ${value ? 'has-value' : ''} ${invalid ? 'book-input-invalid' : ''}`}
          wrapperClassName="book-datepicker-wrapper"
          calendarClassName="book-datepicker-calendar"
          popperClassName="book-datepicker-popper"
          popperPlacement="bottom-end"
          popperContainer={({ children }) =>
            typeof document !== 'undefined' ? createPortal(children, document.body) : children
          }
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          scrollableYearDropdown
          yearDropdownItemNumber={maxDate.getFullYear() - minDate.getFullYear() + 1}
          maxDate={maxDate}
          minDate={minDate}
          openToDate={openToDate}
          autoComplete="off"
          ariaLabel={`Traveller ${travelerIndex + 1} date of birth`}
        />
        <button
          type="button"
          className="book-input-icon-btn"
          aria-label={`Open calendar for traveller ${travelerIndex + 1} date of birth`}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          <Calendar size={18} aria-hidden />
        </button>
      </div>
      {errorMessage ? <p className="book-field-error">{errorMessage}</p> : null}
    </div>
  );
}

export default function Book() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [travelerCount, setTravelerCount] = useState(1);
  const [travelers, setTravelers] = useState([emptyTraveler()]);
  const [travelPackage, setTravelPackage] = useState('bus');
  const [stayOption, setStayOption] = useState('quad');
  const [joinFromPackageId, setJoinFromPackageId] = useState(null);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [addOnQuantities, setAddOnQuantities] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedTraveller, setExpandedTraveller] = useState({ 0: true });
  const [detailsValidationTouched, setDetailsValidationTouched] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [registering, setRegistering] = useState(false);

  const passedOptions = location.state;
  const queryOptions = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const travelPackage = String(params.get('travelPackage') || '').trim();
    const stayOption = String(params.get('stayOption') || '').trim();
    const joinFromPackageId = String(params.get('joinFromPackageId') || '').trim();
    const eventSlug = String(params.get('eventSlug') || '').trim();
    const fromEvent = String(params.get('fromEvent') || '').trim();
    return {
      travelPackage: travelPackage || null,
      stayOption: stayOption || null,
      joinFromPackageId: joinFromPackageId || null,
      eventSlug: eventSlug || null,
      fromEvent: fromEvent === '1' || fromEvent.toLowerCase() === 'true'
    };
  }, [location.search]);
  const toggleTraveller = (i) => setExpandedTraveller((prev) => ({ ...prev, [i]: !prev[i] }));

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const prevRestoration = window.history.scrollRestoration;
    try {
      window.history.scrollRestoration = 'manual';
    } catch {
      // ignore
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const raf = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => {
      window.cancelAnimationFrame(raf);
      try {
        window.history.scrollRestoration = prevRestoration;
      } catch {
        // ignore
      }
    };
  }, [batchId]);

  useEffect(() => {
    setCreatedBooking(null);
    setStep(1);
    setDetailsValidationTouched(false);
    setRegistering(false);
    setSubmitting(false);
  }, [batchId, location.key]);

  useEffect(() => {
    if (!batchId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await dynamicAPI.getEventBatchById(batchId);
        const data = res.data?.data;
        if (cancelled || !data) return;
        setBatch(data);
        const pkg = data.trek?.packages?.find((p) => p.isMain) || data.trek?.packages?.[0];
        if (pkg) setJoinFromPackageId(pkg.id);
        const incomingTravelPackage = passedOptions?.travelPackage || queryOptions.travelPackage;
        const incomingStayOption = passedOptions?.stayOption || queryOptions.stayOption;
        const incomingJoinFromPackageId = passedOptions?.joinFromPackageId || queryOptions.joinFromPackageId;
        if (incomingTravelPackage) setTravelPackage(incomingTravelPackage);
        if (incomingStayOption) setStayOption(incomingStayOption);
        if (incomingJoinFromPackageId) setJoinFromPackageId(incomingJoinFromPackageId);
        const stayOpts = Array.isArray(data.trek?.stayOptions) && data.trek.stayOptions.length > 0 ? data.trek.stayOptions : [];
        if (stayOpts.length > 0 && !incomingStayOption) {
          setStayOption(String(stayOpts[0].id || ''));
        }
      } catch {
        if (!cancelled) setBatch(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [batchId, passedOptions, queryOptions]);

  useEffect(() => {
    if (!batch?.id) return;
    trackBeginCheckout({
      batchId: batch.id,
      trekName: batch.trek?.name || 'Trek Booking',
      price: batch.fee || batch.price
    });
  }, [batch?.id]);

  useEffect(() => {
    if (!batch?.trek?.id) return;
    let cancelled = false;
    addOnsAPI.getByTrekId(batch.trek.id).then((res) => {
      if (cancelled) return;
      const list = res.data?.data || [];
      setAvailableAddOns(list);
      setAddOnQuantities((prev) => {
        const next = { ...prev };
        list.forEach((a) => { if (next[a.id] === undefined) next[a.id] = 0; });
        return next;
      });
    }).catch(() => { if (!cancelled) setAvailableAddOns([]); });
    return () => { cancelled = true; };
  }, [batch?.trek?.id]);

  useEffect(() => {
    const next = Array.from({ length: travelerCount }, (_, i) => travelers[i] || emptyTraveler());
    setTravelers(next);
  }, [travelerCount]);

  const trek = batch?.trek;
  const packages = filterPackagesForLocalCamps(
    (trek?.packages || []).filter((p) => p.isEnabled !== false && !p.hidden),
    trek
  ).sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0));
  const selectedPkg = packages.find((p) => p.id === joinFromPackageId) || packages[0];
  const effectiveTravelPackage = inferTravelPackageFromText(
    `${selectedPkg?.description || ''} ${selectedPkg?.name || ''}`
  ) || travelPackage;
  const basePrice = Number(selectedPkg?.price ?? 0) || 0;
  const travelAdjust =
    Number(trek?.travelBusAdjust ?? 0) * (effectiveTravelPackage === 'bus' ? 1 : 0) +
    Number(trek?.travelTrainAdjust ?? 0) * (effectiveTravelPackage === 'train' ? 1 : 0) +
    Number(trek?.travelFlightAdjust ?? 0) * (effectiveTravelPackage === 'flight' ? 1 : 0);
  const stayOptionsList = Array.isArray(trek?.stayOptions) && trek.stayOptions.length > 0
    ? trek.stayOptions.map((o) => ({ id: String(o.id || ''), name: String(o.name || ''), priceAdjust: Number(o.priceAdjust) || 0 }))
    : [];
  const stayAdjust = stayOptionsList.length > 0
    ? (stayOptionsList.find((o) => o.id === stayOption)?.priceAdjust ?? 0)
    : (Number(trek?.stayQuadAdjust ?? 0) * (stayOption === 'quad' ? 1 : 0) +
       Number(trek?.stayTripleAdjust ?? 0) * (stayOption === 'triple' ? 1 : 0) +
       Number(trek?.stayDoubleAdjust ?? 0) * (stayOption === 'double' ? 1 : 0));
  const perPerson = basePrice + travelAdjust + stayAdjust;
  const subtotal = perPerson * travelerCount;
  const mainPackageForDuration = packages.find((p) => p.isMain) || packages[0] || null;
  const {
    durationDays,
    durationNights,
    startDate: packageStartDateKey,
    endDate: packageEndDateKey
  } = resolveJoinPackageOrderSummary(selectedPkg, {
    mainPackage: mainPackageForDuration,
    batchStartDate: batch?.startDate,
    batchEndDate: batch?.endDate,
    allScheduleDays: trek?.scheduleDays || []
  });
  const packageStartDate = packageStartDateKey ? parseLocalYmd(packageStartDateKey) : null;
  // For initial booking flow we ignore add-ons; they will be handled separately later.
  const addOnTotal = 0;
  const totalAmount = subtotal;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const departureDate = packageStartDate
    ? new Date(packageStartDate.getFullYear(), packageStartDate.getMonth(), packageStartDate.getDate())
    : null;
  const daysUntilDeparture = departureDate ? Math.ceil((departureDate - today) / (24 * 60 * 60 * 1000)) : null;
  const isWithinAdvanceWindow =
    daysUntilDeparture !== null && daysUntilDeparture <= ADVANCE_WINDOW_DAYS;
  const advancePaymentEnabled = trek?.advancePaymentEnabled !== false;
  const perPersonSubtotal = subtotal / Math.max(1, travelerCount);
  const perPersonAdvance = isWithinAdvanceWindow
    ? perPersonSubtotal
    : !advancePaymentEnabled
      ? perPersonSubtotal
    : perPersonSubtotal > ADVANCE_CAP
      ? ADVANCE_CAP
      : perPersonSubtotal;
  const advanceAmount = isWithinAdvanceWindow ? totalAmount : perPersonAdvance * travelerCount;
  const remainingAmount = Math.max(0, totalAmount - advanceAmount);
  const onlineAdvance = onlineTotalWithGatewayFee(advanceAmount);
  const onlineFull = onlineTotalWithGatewayFee(totalAmount);
  const isOnlinePaymentBlocked = bookingEventClosedForPayment({
    batch,
    joinFromPackageId,
    stayOption,
    travelPackage: effectiveTravelPackage
  });
  const showFullOnlineOption = !isOnlinePaymentBlocked && advancePaymentEnabled && !isWithinAdvanceWindow && remainingAmount > 0;
  const initialOnlinePaymentFor =
    isWithinAdvanceWindow || !advancePaymentEnabled ? 'remaining' : 'advance';

  const formatInr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
  const remainingDueDate = remainingDueDateFromDeparture(departureDate);
  const travelModeLabel = effectiveTravelPackage === 'bus'
    ? 'Bus'
    : effectiveTravelPackage === 'train'
      ? 'Non AC Sleeper Train'
      : 'Flight';
  const travellingPackageLabel =
    plainTextFromHtml(selectedPkg?.description) ||
    (travelModeLabel ? `${travelModeLabel} travel` : '');
  const joinFromName = String(selectedPkg?.name || '').trim();
  const joinReportingNote = plainTextFromHtml(selectedPkg?.reportingMessage);
  const trekImageUrl = trek?.media?.[0]?.url || trek?.brochureUrl || '';
  const knowBeforeItems = (trek?.instructions || [])
    .map((item) => ({
      title: String(item?.name || '').trim(),
      text: plainTextFromHtml(item?.description)
    }))
    .filter((item) => item.title || item.text);

  const setAddOnQuantity = (addOnId, delta) => {
    setAddOnQuantities((prev) => {
      const next = { ...prev };
      const cur = next[addOnId] ?? 0;
      next[addOnId] = Math.max(0, cur + delta);
      return next;
    });
  };

  const updateTraveler = (index, field, value) => {
    setTravelers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const trekMinAge = trek?.minAge ?? trek?.min_age ?? null;
  const trekMaxAge = trek?.maxAge ?? trek?.max_age ?? null;
  const trekAgeRangeLabel = formatTrekAgeRangeLabel(trekMinAge, trekMaxAge);
  const ageOnDepartureDate = departureDate || today;
  const dobBounds = useMemo(
    () =>
      dobBoundsForTrekAgeRange({
        minAge: trekMinAge,
        maxAge: trekMaxAge,
        referenceDate: ageOnDepartureDate
      }),
    [trekMinAge, trekMaxAge, ageOnDepartureDate]
  );

  const travelerErrors = travelers.slice(0, travelerCount).map((t) => {
    let birthdateMessage = '';
    if (!isValidTravelerBirthdate(t.birthdate)) {
      birthdateMessage = 'Enter a valid date of birth (DD/MM/YYYY).';
    } else if (trekAgeRangeLabel) {
      const ageCheck = validateAgeForTrekRange({
        birthdate: birthdateForApi(t.birthdate),
        minAge: trekMinAge,
        maxAge: trekMaxAge,
        referenceDate: ageOnDepartureDate
      });
      if (!ageCheck.ok) birthdateMessage = ageCheck.message;
    }
    return {
      firstName: !String(t.firstName || '').trim(),
      lastName: !String(t.lastName || '').trim(),
      phone: !isValidMobile(t.phone),
      birthdate: Boolean(birthdateMessage),
      birthdateMessage,
      gender: !isValidTravelerGender(t.gender)
    };
  });

  const hasTravelerErrors = travelerErrors.some(
    (e) => e.firstName || e.lastName || e.phone || e.birthdate || e.gender
  );

  const canProceedDetails = () => {
    if (!termsAccepted) return false;
    return !hasTravelerErrors;
  };

  const buildBookingPayload = (payMethod = 'office') => ({
    batchId: batch.id,
    payMethod,
    travelerCount,
    travelers: travelers.slice(0, travelerCount).map((t) => ({
      firstName: capitalizeNamePart(t.firstName),
      lastName: capitalizeNamePart(t.lastName),
      phone: normalizeMobile(t.phone),
      birthdate: birthdateForApi(t.birthdate),
      gender: t.gender || undefined
    })),
    travelPackage: effectiveTravelPackage,
    stayOption,
    joinFromPackageId: joinFromPackageId || undefined,
    addOns: [],
    termsAccepted: true
  });

  const confirmedPathFromBooking = (booking) => {
    const raw = booking?.bookingCode ?? booking?.booking_code;
    const code = raw != null ? String(raw).trim().replace(/^#/, '') : '';
    const seg = code || booking?.id;
    return `/bookings/confirmed/${encodeURIComponent(seg)}`;
  };

  const handleRegisterNow = async () => {
    setDetailsValidationTouched(true);
    if (!canProceedDetails() || !batch?.id) return;
    setRegistering(true);
    try {
      const res = await bookingAPI.create(buildBookingPayload('office'));
      const created = res.data?.data;
      if (!created?.id) {
        window.alert('Registration failed. Please try again.');
        return;
      }
      try {
        await refreshUser();
      } catch {
        /* non-blocking */
      }
      setCreatedBooking(created);
      setStep(2);
    } catch (err) {
      const data = err.response?.data;
      window.alert(data?.message || 'Registration failed. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  const handlePayment = async (payMethod, { onlinePaymentFor = 'advance' } = {}) => {
    const booking = createdBooking;
    if (!booking?.id) {
      window.alert('Please complete registration first.');
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const confirmedPath = confirmedPathFromBooking(booking);
      if (payMethod === 'online') {
        try {
          await openRazorpayBookingPayment({ booking, paymentFor: onlinePaymentFor });
          navigate(confirmedPath, { replace: true });
        } catch (paymentErr) {
          window.alert(paymentErr?.response?.data?.message || paymentErr.message || 'Payment not completed. Your booking is saved as pending.');
          navigate(`${confirmedPath}?payment=office`, { replace: true });
        }
        return;
      }
      navigate(`${confirmedPath}?payment=office`, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !batch) {
    return (
      <div className="book-page">
        <div className="book-inner">
          {loading ? (
            <div className="book-loading">
              <LoadingIndicator label="Loading..." />
            </div>
          ) : (
            <p className="book-loading">Batch not found.</p>
          )}
          {!loading && !batch && <Link to="/events">Back to Events</Link>}
        </div>
      </div>
    );
  }

  const eventSlug = passedOptions?.eventSlug ?? queryOptions.eventSlug ?? batch?.trek?.slug ?? '';
  const backToEventUrl = eventSlug ? `/events/${eventSlug}?batch=${batch.id}` : '/events';
  const handleBack = (e) => {
    e.preventDefault();
    if (window.history.length > 1 && (passedOptions?.fromEvent || queryOptions.fromEvent)) {
      navigate(-1);
    } else {
      navigate(backToEventUrl);
    }
  };

  return (
    <div className="book-page">
      <div className="book-inner">
        <button type="button" className="book-back" onClick={handleBack} style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>
          <ArrowLeft size={18} /> Back
        </button>

        <div className="book-steps-wrap">
          <div className="book-stepper">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="book-stepper-item">
                <div className="book-stepper-block">
                  <div className={`book-stepper-circle ${step > s.id ? 'done' : step === s.id ? 'current' : 'upcoming'}`}>
                    {step > s.id ? <Check size={18} /> : s.id}
                  </div>
                  <span className={`book-stepper-label ${step === s.id ? 'current' : ''}`}>{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && <span className={`book-stepper-line ${step > s.id ? 'active' : ''}`} />}
              </div>
            ))}
          </div>
          <a href="/contact" className="book-need-help">
            <HelpCircle size={16} /> Need Help?
          </a>
        </div>

        {step === 1 && (
          <div className="book-details-layout">
            <div className="book-details-form">
              <div className="book-details-card">
                <h2 className="book-details-card-title">Number of Travellers</h2>
                <div className="book-traveler-count">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`book-count-btn ${travelerCount === n ? 'active' : ''}`}
                      onClick={() => setTravelerCount(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {trekAgeRangeLabel ? (
                <p className="book-age-range-note">
                  Eligible age for this trek: <strong>{trekAgeRangeLabel}</strong> (on departure date).
                </p>
              ) : null}

              <div className="book-traveler-accordion">
                {travelers.slice(0, travelerCount).map((t, i) => {
                  const errs = travelerErrors[i] || {
                    firstName: false,
                    lastName: false,
                    phone: false,
                    birthdate: false,
                    birthdateMessage: '',
                    gender: false
                  };
                  return (
                    <div key={i} className="book-traveler-card-v2">
                      <div className="book-traveler-card-header">
                        <span className="book-traveler-card-title">Traveller {i + 1}</span>
                      </div>
                      <div className="book-traveler-card-body">
                        <div className="book-form-grid">
                            <div className="book-form-field">
                              <label className="book-field-label">First Name <span className="book-required-star">*</span></label>
                              <input
                                className={`book-input ${detailsValidationTouched && errs.firstName ? 'book-input-invalid' : ''}`}
                                placeholder="First Name"
                                value={t.firstName}
                                onChange={(e) => updateTraveler(i, 'firstName', e.target.value)}
                              />
                              {detailsValidationTouched && errs.firstName ? <p className="book-field-error">First name is required.</p> : null}
                            </div>
                            <div className="book-form-field">
                              <label className="book-field-label">Last Name <span className="book-required-star">*</span></label>
                              <input
                                className={`book-input ${detailsValidationTouched && errs.lastName ? 'book-input-invalid' : ''}`}
                                placeholder="Last Name"
                                value={t.lastName}
                                onChange={(e) => updateTraveler(i, 'lastName', e.target.value)}
                              />
                              {detailsValidationTouched && errs.lastName ? <p className="book-field-error">Last name is required.</p> : null}
                            </div>
                            <div className="book-form-field book-form-field-full">
                              <label className="book-field-label">Phone Number <span className="book-required-star">*</span></label>
                              <div className="book-phone-row">
                                <div className="book-phone-prefix-v2">
                                  <select defaultValue="+91" aria-label="Country code" className="book-input">
                                    <option value="+91">+91 (India)</option>
                                    <option value="+1">+1 (USA)</option>
                                    <option value="+44">+44 (UK)</option>
                                  </select>
                                  <span className="book-select-arrow" aria-hidden>▼</span>
                                </div>
                                <input
                                  className={`book-input book-phone-input-v2 ${detailsValidationTouched && errs.phone ? 'book-input-invalid' : ''}`}
                                  placeholder="Phone Number"
                                  type="tel"
                                  value={t.phone}
                                  onChange={(e) => updateTraveler(i, 'phone', normalizeMobile(e.target.value))}
                                  inputMode="numeric"
                                  maxLength={MOBILE_DIGITS}
                                />
                              </div>
                              {detailsValidationTouched && errs.phone ? <p className="book-field-error">Enter valid 10-digit mobile number.</p> : null}
                            </div>
                            <BookTravelerDobField
                              id={`traveler-${i}-birthdate`}
                              value={t.birthdate}
                              onChange={(ymd) => updateTraveler(i, 'birthdate', ymd)}
                              invalid={detailsValidationTouched && errs.birthdate}
                              errorMessage={
                                detailsValidationTouched && errs.birthdate
                                  ? errs.birthdateMessage || 'Enter a valid date of birth (DD/MM/YYYY).'
                                  : null
                              }
                              travelerIndex={i}
                              minDate={dobBounds.minDate}
                              maxDate={dobBounds.maxDate}
                            />
                            <div className="book-form-field">
                              <label className="book-field-label" htmlFor={`traveler-${i}-gender`}>
                                Gender <span className="book-required-star">*</span>
                              </label>
                              <div className="book-input-icon-wrap">
                                <select
                                  id={`traveler-${i}-gender`}
                                  value={t.gender}
                                  onChange={(e) => updateTraveler(i, 'gender', e.target.value)}
                                  className={`book-input book-select ${detailsValidationTouched && errs.gender ? 'book-input-invalid' : ''}`}
                                >
                                  <option value="">Select gender</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                  <option value="Other">Other</option>
                                </select>
                                <span className="book-select-arrow" aria-hidden>▼</span>
                              </div>
                              {detailsValidationTouched && errs.gender ? (
                                <p className="book-field-error">Gender is required.</p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                    </div>
                  );
                })}
              </div>

              {knowBeforeItems.length > 0 ? (
                <div className="book-know-before-v2">
                  <h3 className="book-know-before-title">Know Before You Go</h3>
                  <ul className="book-know-before-list">
                    {knowBeforeItems.map((item, idx) => (
                      <li key={idx}>
                        <span className="book-kb-icon book-kb-icon-exclusion" aria-hidden>
                          <X size={14} />
                        </span>
                        {item.title && item.text ? `${item.title}: ${item.text}` : item.title || item.text}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

            </div>

            <aside className="book-order-summary-side">
              <div className="book-order-summary-card">
                <h3 className="book-order-summary-title">Order Summary</h3>
                <div className="book-order-summary-inner">
                  {trekImageUrl ? (
                    <div className="book-order-image-v2">
                      <img src={trekImageUrl} alt={trek?.name || 'Event'} />
                    </div>
                  ) : null}
                  <div className="book-order-meta">
                    <h4 className="book-order-trek-name">{trek?.name}</h4>
                    <p className="book-order-duration">{durationDays} Days {durationNights} Nights</p>
                    {packageStartDateKey && packageEndDateKey && (
                      <p className="book-order-dates">{formatDate(packageStartDateKey)} – {formatDate(packageEndDateKey)}</p>
                    )}
                    {(joinFromName || travellingPackageLabel) ? (
                      <p className="book-order-origin">
                        {joinFromName || '—'}
                        {travellingPackageLabel ? (
                          <span className="book-order-origin-with"> {travellingPackageLabel}</span>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                  <p className="book-order-note">*Fill up details and proceed with payment</p>
                  <div className="book-terms-row book-terms-row--sidebar">
                    <input id="book-terms-cb" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="book-terms-checkbox" />
                    <label htmlFor="book-terms-cb" className="book-terms-label">
                      I accept participation terms and conditions{' '}
                      <Link
                        to="/terms"
                        className="book-terms-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        (Click Here)
                      </Link>
                    </label>
                  </div>
                  {detailsValidationTouched && !termsAccepted ? (
                    <p className="book-field-error book-terms-error">Please accept terms and conditions to continue.</p>
                  ) : null}
                  <div className="book-order-divider" />
                  <div className="book-order-breakdown-list">
                    <div className="book-order-breakdown-row">
                      <span>Package Price</span>
                      <span>{formatInr(totalAmount)}</span>
                    </div>
                    <div className="book-order-divider" />
                    <div className="book-order-total-row">
                      <span>Total Amount</span>
                      <span className="book-order-total-amount">{formatInr(totalAmount)}</span>
                    </div>
                  </div>
                  {detailsValidationTouched && !canProceedDetails() ? (
                    <p className="book-register-help">Fill all mandatory traveller fields marked with * to continue.</p>
                  ) : null}
                  <button
                    type="button"
                    className="book-register-now"
                    onClick={handleRegisterNow}
                    disabled={!canProceedDetails() || registering}
                  >
                    {registering ? 'Registering…' : 'Register Now'}
                  </button>
                </div>
              </div>
            </aside>
          </div>
        )}

        {step === 2 && (
          <div className="book-section book-payment-section">
            <div className="book-payment-main">
              <h2>Payment</h2>
              <p className="book-payment-intro">
                {!advancePaymentEnabled
                  ? 'Advance payment is not available for this trip, so full amount is payable now (office or online).'
                  : isWithinAdvanceWindow
                  ? `Departure is within ${ADVANCE_WINDOW_DAYS} days, so full amount is payable now (office or online).`
                  : travelerCount > 1
                    ? `For trips over ₹5,000 per person we collect an advance of ₹5,000 per participant (₹${(ADVANCE_CAP * travelerCount).toLocaleString('en-IN')} for ${travelerCount} travellers). Remaining amount is due ${REMAINING_DUE_DAYS_BEFORE_DEPARTURE} days before trip start.`
                    : `For trips over ₹5,000 we collect an advance of ₹5,000 (payable at office or online). Remaining amount is due ${REMAINING_DUE_DAYS_BEFORE_DEPARTURE} days before trip start.`}
              </p>
              <div className="book-payment-summary">
                <p><strong>{isWithinAdvanceWindow || !advancePaymentEnabled ? 'Amount to pay now:' : 'Advance to pay now:'}</strong> {formatInr(advanceAmount)}</p>
                {remainingAmount > 0 && (
                  <p><strong>Remaining (due by {formatDate(remainingDueDate)}):</strong> {formatInr(remainingAmount)}</p>
                )}
              </div>

              <div className="book-review-card">
                <div className="book-review-header">
                  <h3>Review Booking</h3>
                  <button type="button" className="book-edit-details" onClick={() => setStep(1)}>
                    Edit Details
                  </button>
                </div>
                <div className="book-review-grid">
                  <div className="book-review-block">
                    <span className="book-review-label"><Mountain size={14} /> TREK SELECTION</span>
                    <strong>{trek?.name}</strong>
                    <span className="book-review-meta">
                      Dates: {packageStartDateKey && packageEndDateKey ? `${formatDate(packageStartDateKey)} - ${formatDate(packageEndDateKey)}` : '—'}
                    </span>
                  </div>
                  <div className="book-review-block">
                    <span className="book-review-label">TRAVELLING PACKAGE</span>
                    <strong>{travellingPackageLabel || '—'}</strong>
                    <span className="book-review-label" style={{ marginTop: 8 }}>STAY TYPE</span>
                    <strong>
                      {formatStayOptionLabel(
                        stayOptionsList.find((o) => o.id === stayOption)?.name,
                        stayOption
                      )}
                    </strong>
                  </div>
                  {joinFromName ? (
                    <div className="book-review-block">
                      <span className="book-review-label"><Flag size={14} /> JOIN US FROM</span>
                      <strong>{joinFromName}</strong>
                      {joinReportingNote ? (
                        <span className="book-review-meta">{joinReportingNote}</span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="book-review-block">
                    <span className="book-review-label"><Users size={14} /> PARTICIPANTS LIST ({travelerCount})</span>
                    <ul className="book-participants-list">
                      {travelers.map((t, i) => (
                        <li key={i}>
                          <span className={i === 0 ? 'book-participant-primary' : ''}>
                            {t.firstName} {t.lastName}
                          </span>
                          {i === 0 && <em>Primary</em>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <p className="book-payment-note">By proceeding you agree to the Booking Terms and Safety Guidelines.</p>
            </div>

            <div className="book-order-summary">
              <h3>Order Summary</h3>
              {trekImageUrl ? (
                <div className="book-order-image">
                  <img src={trekImageUrl} alt={trek?.name || 'Event'} />
                </div>
              ) : null}
              <h4>{trek?.name}</h4>
              <p><Calendar size={14} /> {packageStartDateKey && packageEndDateKey ? `${formatDate(packageStartDateKey)} – ${formatDate(packageEndDateKey)}` : '—'}</p>
              <p><Users size={14} /> {travelerCount} Traveller(s)</p>
              <p><Bus size={14} /> Travelling Package: {travellingPackageLabel}</p>
              <p>
                <Bed size={14} />{' '}
                {formatStayOptionLabel(
                  stayOptionsList.find((o) => o.id === stayOption)?.name,
                  stayOption
                )}
              </p>
              {joinFromName ? <p><MapPin size={14} /> Join Us From: {joinFromName}</p> : null}
              {joinReportingNote ? <p className="book-order-reporting-note">{joinReportingNote}</p> : null}
              <div className="book-order-breakdown">
                <span>Package ({travelerCount}×)</span>
                <span>{formatInr(subtotal)}</span>
              </div>
              <div className="book-order-total">
                <span>Total Amount</span>
                <span>{formatInr(totalAmount)}</span>
              </div>
              <div className="book-payment-buttons">
                <button
                  type="button"
                  className="book-pay-office"
                  onClick={() => handlePayment('office')}
                  disabled={submitting}
                >
                  <span className="book-pay-cta-label">Pay at Office</span>
                  <span className="book-pay-cta-sep" aria-hidden>—</span>
                  <span className="book-pay-cta-amount">{formatInr(advanceAmount)}</span>
                </button>
                {!isOnlinePaymentBlocked && (
                  <>
                    <button
                      type="button"
                      className="book-pay-online book-pay-online--with-gateway"
                      onClick={() => handlePayment('online', { onlinePaymentFor: initialOnlinePaymentFor })}
                      disabled={submitting}
                    >
                      <span className="book-pay-online-stack">
                        <span className="book-pay-online-stack-main">
                          <span className="book-pay-cta-label">
                            {showFullOnlineOption ? 'Pay advance online' : 'Pay Online'}
                          </span>
                          <span className="book-pay-cta-sep" aria-hidden>—</span>
                          <span className="book-pay-cta-amount">{formatInr(onlineAdvance.payableTotal)}</span>
                        </span>
                        <small className="book-pay-cta-gateway-hint">
                          +2% Transaction Charges({formatInr(onlineAdvance.gatewayCharge)})
                        </small>
                      </span>
                    </button>
                    {showFullOnlineOption && (
                      <button
                        type="button"
                        className="book-pay-online book-pay-online--with-gateway book-pay-online--full-total"
                        onClick={() => handlePayment('online', { onlinePaymentFor: 'remaining' })}
                        disabled={submitting}
                      >
                        <span className="book-pay-online-stack">
                          <span className="book-pay-online-stack-main">
                            <span className="book-pay-cta-label">Pay full amount online</span>
                            <span className="book-pay-cta-sep" aria-hidden>—</span>
                            <span className="book-pay-cta-amount">{formatInr(onlineFull.payableTotal)}</span>
                          </span>
                          <small className="book-pay-cta-gateway-hint">
                            Full trip total incl. +2% ({formatInr(onlineFull.gatewayCharge)} charges)
                          </small>
                        </span>
                      </button>
                    )}
                  </>
                )}
              </div>
              {isOnlinePaymentBlocked && (
                <p className="book-order-reporting-note" style={{ marginTop: 12, color: '#dc2626' }}>
                  Online payment is unavailable because the selected package or stay option is closed for this departure. Please complete your payment at our office.
                </p>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
